// One asset row on the render-activity board. Collapsed view is dense
// and scannable; expand reveals ids / intent / prediction / asset link.
// Copy uses the server-built `diagnostic` string verbatim.

import { useState } from 'react';
import {
  Box, HStack, VStack, Text, Badge, Button, Link, Collapse, SimpleGrid, useToast
} from '@chakra-ui/react';
import { ChevronDown, ChevronRight, Copy, ExternalLink } from 'lucide-react';
import type { RenderActivityRow as Row } from './types';
import {
  copyText,
  formatDurationSec,
  formatMs,
  formatRelativeIso,
  formatUnknown,
  stageAgeColor,
  statusColorScheme
} from './format';

type Props = {
  row: Row;
};

export function RenderActivityRow({ row }: Props) {
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const onCopy = async () => {
    try {
      await copyText(row.diagnostic);
      toast({ title: 'Diagnostic copied', status: 'success', duration: 2000 });
    } catch (e) {
      toast({
        title: 'Copy failed',
        description: e instanceof Error ? e.message : 'Clipboard unavailable',
        status: 'error',
        duration: 4000
      });
    }
  };

  const timings = [
    row.timingsMs.derive != null ? `derive ${formatMs(row.timingsMs.derive)}` : null,
    row.timingsMs.render != null ? `render ${formatMs(row.timingsMs.render)}` : null,
    row.timingsMs.upload != null ? `upload ${formatMs(row.timingsMs.upload)}` : null
  ].filter(Boolean).join(' · ') || '—';

  return (
    <Box
      borderLeftWidth="3px"
      borderLeftColor={row.stalled ? 'red.500' : 'transparent'}
      bg={row.stalled ? 'red.50' : undefined}
      _hover={{ bg: row.stalled ? 'red.50' : 'gray.50' }}
    >
      <HStack
        align="flex-start"
        spacing={3}
        px={4}
        py={3}
        cursor="pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <Box pt="2px" color="brand.muted" flexShrink={0}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Box>

        <VStack align="stretch" spacing={1.5} flex={1} minW={0}>
          {/* Status + stage line */}
          <HStack spacing={2} flexWrap="wrap">
            <Badge
              colorScheme={statusColorScheme(row.status)}
              fontSize="9px"
              textTransform="uppercase"
            >
              {row.status || '—'}
            </Badge>
            {row.stalled && (
              <Badge colorScheme="red" fontSize="9px" textTransform="uppercase">
                Stalled
              </Badge>
            )}
            {row.derivedFromMaster && (
              <Badge
                colorScheme="cyan"
                fontSize="9px"
                textTransform="uppercase"
                title="Cropped from the 9:16 master — not a separate generation bill"
              >
                From master
              </Badge>
            )}
            <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>
              {row.stage || '—'}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="700"
              color={stageAgeColor(row.stageAgeSec, row.stalled)}
              flexShrink={0}
            >
              {formatDurationSec(row.stageAgeSec)}
            </Text>
          </HStack>

          {/* Meta scan line */}
          <HStack spacing={3} flexWrap="wrap" fontSize="11px" color="brand.muted">
            <Text>
              <Text as="span" fontWeight="700" color="brand.ink">{row.kind || '—'}</Text>
              {' · '}
              {row.platformFormat || '—'}
              {' · '}
              {row.aspectRatio || '—'}
            </Text>
            <Text>
              {row.pipeline || '—'}
              {row.model ? ` / ${row.model}` : ''}
            </Text>
            <Text>
              attempts {row.attempts != null ? row.attempts : '—'}
            </Text>
            <Text>{timings}</Text>
            {row.requestedBy && (
              <Text noOfLines={1}>{row.requestedBy}</Text>
            )}
            {row.template && (
              <Text fontWeight="600" color="brand.ink" noOfLines={1}>
                {row.template}
              </Text>
            )}
          </HStack>

          {row.error && (
            <Text fontSize="12px" color="red.600" fontWeight="600" noOfLines={2}>
              {row.error}
            </Text>
          )}
        </VStack>

        <Button
          size="xs"
          variant="outline"
          leftIcon={<Copy size={12} />}
          flexShrink={0}
          onClick={(e) => {
            e.stopPropagation();
            void onCopy();
          }}
        >
          Copy
        </Button>
      </HStack>

      <Collapse in={open} animateOpacity>
        <Box
          px={4}
          pb={4}
          pl="44px"
          borderTopWidth="1px"
          borderColor={row.stalled ? 'red.100' : 'brand.border'}
        >
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3} pt={3} fontSize="12px">
            <IdField label="assetId" value={row.assetId} />
            <IdField label="runId" value={row.ids.runId} />
            <IdField label="campaignId" value={row.ids.campaignId} />
            <IdField label="productId" value={row.ids.productId} />
            <IdField label="mediaId" value={row.ids.mediaId} />
            <IdField label="conceptId" value={row.ids.conceptId} />
            <IdField label="brandId" value={row.ids.brandId} />
            <IdField label="predictionId" value={row.predictionId} mono />
          </SimpleGrid>

          <HStack spacing={4} mt={3} fontSize="11px" color="brand.muted" flexWrap="wrap">
            <Text>queued {formatRelativeIso(row.queuedAt)}</Text>
            <Text>rendered {formatRelativeIso(row.renderedAt)}</Text>
            <Text>updated {formatRelativeIso(row.updatedAt)}</Text>
            {row.run && (
              <Text>
                run {row.run.status || '—'}: {formatCount(row.run.succeeded)}/
                {formatCount(row.run.total)} ok
                {row.run.failed != null && row.run.failed > 0
                  ? `, ${row.run.failed} failed`
                  : ''}
                {row.run.skipped != null && row.run.skipped > 0
                  ? `, ${row.run.skipped} skipped`
                  : ''}
              </Text>
            )}
          </HStack>

          {row.derivedFromMaster && (
            <Box
              mt={3}
              px={3}
              py={2}
              bg="cyan.50"
              borderWidth="1px"
              borderColor="cyan.200"
              borderRadius="md"
            >
              <Text fontSize="11px" fontWeight="700" color="cyan.800" textTransform="uppercase" letterSpacing="0.05em">
                Derived from 9:16 master
              </Text>
              <Text fontSize="12px" color="brand.ink" mt={0.5}>
                This asset was cropped from the master video — it was not billed as its own generation.
              </Text>
            </Box>
          )}

          {row.intent && (
            <Box mt={3}>
              <Text
                fontSize="10px"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.08em"
                color="brand.muted"
                mb={1}
              >
                Intent
              </Text>
              <VStack align="stretch" spacing={0.5} fontSize="12px" fontFamily="mono">
                <Text>
                  <Text as="span" color="brand.muted">requested </Text>
                  {formatUnknown(row.intent.requested)}
                </Text>
                <Text>
                  <Text as="span" color="brand.muted">delivered </Text>
                  {formatUnknown(row.intent.delivered)}
                </Text>
                <Text>
                  <Text as="span" color="brand.muted">fellBackFrom </Text>
                  {formatUnknown(row.intent.fellBackFrom)}
                </Text>
                <Text>
                  <Text as="span" color="brand.muted">dropped </Text>
                  {Array.isArray(row.intent.dropped) && row.intent.dropped.length > 0
                    ? row.intent.dropped.map(formatUnknown).join(', ')
                    : '—'}
                </Text>
              </VStack>
            </Box>
          )}

          {row.assetUrl && (
            <Box mt={3}>
              <Link
                href={row.assetUrl}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                fontSize="12px"
                color="rsViolet.600"
                fontWeight="600"
                display="inline-flex"
                alignItems="center"
                gap={1}
              >
                Open asset <ExternalLink size={12} />
              </Link>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/** Absent / non-finite counters render as em-dash — never blank or 0. */
function formatCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return String(n);
}

function IdField({
  label,
  value,
  mono
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <Box minW={0}>
      <Text
        fontSize="9px"
        fontWeight="800"
        textTransform="uppercase"
        letterSpacing="0.08em"
        color="brand.muted"
        mb={0.5}
      >
        {label}
      </Text>
      <Text
        fontSize="12px"
        color="brand.ink"
        fontFamily={mono ? 'mono' : undefined}
        noOfLines={1}
        title={value || undefined}
      >
        {value || '—'}
      </Text>
    </Box>
  );
}
